import React, { useState, useRef, useEffect, useMemo, useCallback } from "react";
import Select from "react-select";
import DatePicker from "react-datepicker";
import { Modal, ModalHeader, ModalBody, ModalFooter } from "reactstrap";
import moment from "moment";
import ctCountyOptions from "../../../_helpers/ctCountyCodes";
import projActions from "../../../_actions/projActions";
import AlertModal from "../../_common/AlertModal";
import CustomDatePickerInput from "../../_common/CustomDatePickerInput";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useDispatch, useSelector } from "react-redux";
import _ from "lodash";

const PM_PREFIX_OPTIONS = ["C", "D", "G", "H", "L", "M", "N", "R", "S", "T"];

// Builds the postMiles payload for a single PM row. The UI stores prefix and numeric
// in separate state fields; the backend expects them as separate fields too.
const buildPmPayload = (pmData) => ({
  county: pmData?.county,
  route: pmData?.route,
  backPostMile: parseFloat(pmData?.backPostMile) || 0,
  backPostMilePrefix: pmData?.backPostMilePrefix || undefined,
  aheadPostMile: parseFloat(pmData?.aheadPostMile) || 0,
  aheadPostMilePrefix: pmData?.aheadPostMilePrefix || undefined,
});

export default function EditProjectModalV2(props) {
  const { showModal, toggleModal, projectId, refreshProjList } = props;

  const dispatch = useDispatch();

  useEffect(() => dispatch(projActions.fetchProjectTypes()), []);

  const fetching = useSelector((state) => state.projects.fetching || false);

  const projTypes = useSelector((state) => state.projects.projTypes || null);
  const projData = useSelector((state) =>
    projectId ? state.projects.openProjectDetails[projectId] || null : null
  );

  const user = useSelector((state) => state.authentication.user || null);

  const [_data, setData] = useState(projData ? { ...projData } : {});
  useEffect(() => setData(projData ? { ...projData } : {}), [projData]);

  const [childErrors, setChildErrors] = useState({});
  const setChildError = useCallback(
    (key, hasError) =>
      setChildErrors((prev) => {
        if (prev[key] === hasError) return prev;
        return { ...prev, [key]: hasError };
      }),
    []
  );
  const formIsIncomplete = Object.values(childErrors).some(Boolean);

  const [showAlert_success, _setShowAlert_success] = useState(false);
  const toggleAlert_success = () => _setShowAlert_success(!showAlert_success);

  const [showAlert_cancel, _setShowAlert_cancel] = useState(false);
  const toggleAlert_cancel = () => _setShowAlert_cancel(!showAlert_cancel);

  const update = (fieldName, value) => setData({ ..._data, [fieldName]: value });

  const handleClick_submit = async (e) => {
    e.preventDefault();

    if (formIsIncomplete || !_data.district || !_data.ea || !_data.workDescription)
      return;

    const projectData = {
      projectId: _data.projectId || 0,
      createdByUserId: _data.createdByUserId || (user ? user.userId : null),
      createdOn: _data.createdOn || moment().format(),
      district: _data.district,
      ea: _data.ea,
      efis: _data.efis || "",
      projectType: _data.projectType || "OTHER",
      fedId: _data.fedId || "",
      postMiles1: buildPmPayload(_data.postMiles1),
      postMiles2: buildPmPayload(_data.postMiles2),
      postMiles3: buildPmPayload(_data.postMiles3),
      postMiles4: buildPmPayload(_data.postMiles4),
      workDescription: _data.workDescription,
      locationDescription: _data.locationDescription || "",
      calendarType: _data.calendarType || "5d",
      workingDays: _data.workingDays || 0,
      bidOpeningDate: _data.bidOpeningDate ? moment(_data.bidOpeningDate).format() : null,
      contractAwardDate: _data.contractAwardDate
        ? moment(_data.contractAwardDate).format()
        : null,
      contractApprovalDate: _data.contractApprovalDate
        ? moment(_data.contractApprovalDate).format()
        : null,
      firstWorkingDay: _data.firstWorkingDay
        ? moment(_data.firstWorkingDay).format()
        : null,
      engineersEstimate: _data.engineersEstimate || 0,
      useTroMarkup: _data.useTroMarkup || false,
      primeLaborMarkup: _data.primeLaborMarkup || 0,
      primeEquipmentMarkup: _data.primeEquipmentMarkup || 0,
      primeMaterialMarkup: _data.primeMaterialMarkup || 0,
      subcontractorMarkup: _data.subcontractorMarkup || 0,
      subLaborMarkup: _data.subLaborMarkup || 0,
      subEquipmentMarkup: _data.subEquipmentMarkup || 0,
      subMaterialMarkup: _data.subMaterialMarkup || 0,
      projectStatus: _data.projectStatus,
      isPRSMListed: _data.isPRSMListed || false,
      contractKey: _data.contractKey || null,
      contractNo: _data.contractNo || "",
    };

    if (projectId) {
      await dispatch(projActions.update(projectData));
      refreshProjList(false);
    } else {
      await dispatch(projActions.add(projectData));
      refreshProjList(false);
    }

    toggleAlert_success();
  };

  const handleClick_cancel = (e) => {
    e.preventDefault();
    toggleAlert_cancel();
  };

  const handleAlert_success = () => () => {
    toggleAlert_success();
    toggleModal();
  };

  const handleAlert_cancel = (actionType) => () => {
    toggleAlert_cancel();
    if (actionType === "accept") {
      toggleModal();
    }
  };

  const closeBtn = (
    <button className="close" onClick={handleClick_cancel}>
      <FontAwesomeIcon icon="times" className="fa-fw text-white" />
    </button>
  );

  return (
    <React.Fragment>
      <Modal isOpen={showModal} toggle={handleClick_cancel} size={"xl"} fade={false}>
        <ModalHeader className="bg-info text-white" close={closeBtn}>
          <FontAwesomeIcon
            icon={`${projectId ? "edit" : "folder-plus"}`}
            className="fa-fw mr-2"
          />
          {`${projectId ? "Edit " : "Add New "} Project`}
        </ModalHeader>
        <ModalBody>
          <EditProjectModalV2_BasicInfo
            data={_data}
            projTypes={projTypes}
            update={update}
            setFormIsIncomplete={(v) => setChildError("basicInfo", v)}
          />
          <EditProjectModalV2_OtherDetails
            data={_data || {}}
            update={update}
            setFormIsIncomplete={(v) => setChildError("otherDetails", v)}
          />
        </ModalBody>
        <ModalFooter className="bg-lightgray">
          <div className="form-row form-group float-right mr-2 mb-0">
            <button
              type="button"
              name="cancelEditProject"
              id="cancelEditProject"
              className="btn btn-secondary"
              onClick={handleClick_cancel}
              disabled={fetching}
            >
              Cancel
            </button>
            <button
              type="submit"
              name="submitEditProjectForm"
              id="submitEditProjectForm"
              className="btn btn-primary mx-2"
              onClick={handleClick_submit}
              disabled={formIsIncomplete || fetching}
            >
              {fetching && (
                <span className="mr-1">
                  <small>Saving...</small>
                </span>
              )}
              <b>{projectId ? "Save" : "Add Project"}</b>
            </button>
          </div>
        </ModalFooter>
      </Modal>
      {showAlert_success && (
        <AlertModal
          alertTitle="Save Successful"
          alertTitleClass="bg-success text-white"
          alertTitleIcon="check"
          alertText="Project information saved successfully."
          acceptCaption="OK"
          acceptClass="primary"
          provideReject={false}
          handleAlertAction={handleAlert_success}
          showAlertModal={showAlert_success}
          toggleAlertModal={toggleAlert_success}
        />
      )}
      {showAlert_cancel && (
        <AlertModal
          alertTitle={_data ? "Cancel Edit Project" : "Cancel Add Project"}
          alertTitleClass="bg-warning"
          alertTitleIcon="exclamation-triangle"
          alertText="Are you sure you want to cancel?"
          acceptCaption="Yes"
          acceptClass="danger"
          provideReject={true}
          rejectCaption="No"
          rejectClass="secondary"
          handleAlertAction={handleAlert_cancel}
          showAlertModal={showAlert_cancel}
          toggleAlertModal={toggleAlert_cancel}
        />
      )}
    </React.Fragment>
  );
}

function EditProjectModalV2_BasicInfo(props) {
  const { data, projTypes, update, setFormIsIncomplete } = props;

  const projStatusOptions = useRef([
    { value: "ADVERTISED", label: "Advertised" },
    { value: "PRE_CONST", label: "Pre-Const" },
    { value: "IN_CONST", label: "In-Const" },
    { value: "POST_CONST", label: "Post-Const" },
  ]);

  const projTypeOptions = useMemo(
    () =>
      projTypes
        ? _.orderBy(projTypes, "value", "asc").map((pt) => ({
            value: pt.name,
            label: pt.displayName,
          }))
        : null,
    [projTypes]
  );

  const projDistOptions = useRef([
    { value: 1, label: "01" },
    { value: 2, label: "02" },
    { value: 3, label: "03" },
    { value: 4, label: "04" },
    { value: 5, label: "05" },
    { value: 6, label: "06" },
    { value: 7, label: "07" },
    { value: 8, label: "08" },
    { value: 9, label: "09" },
    { value: 10, label: "10" },
    { value: 11, label: "11" },
    { value: 12, label: "12" },
  ]);

  const countyOptions = useRef([
    ...ctCountyOptions,
    { value: "Var", label: "Var - Various" },
  ]);

  const [selectedDistrict, selectDistrict] = useState(
    data && data.district
      ? projDistOptions.current.find((d) => d.value === data.district) || null
      : null
  );
  const [ea, setEa] = useState((data && data.ea) || "");
  const [efis, setEfis] = useState((data && data.efis) || "");
  const [fedId, setFedId] = useState((data && data.fedId) || "");
  const [selectedStatus, selectStatus] = useState(
    data && data.projectStatus
      ? projStatusOptions.current.find(
          (so) => so.value.toLowerCase() === data.projectStatus.toLowerCase()
        ) || null
      : null
  );
  const [selectedType, selectType] = useState(
    data && data.projectType
      ? (projTypeOptions || []).find((to) => to.value === data.projectType) || null
      : null
  );
  const showContractNo = useMemo(
    () => selectedType && selectedType.value !== "DBB_CT",
    [selectedType]
  );

  const [contractNo, setContractNo] = useState((data && data.contractNo) || "");

  const [workDescription, setWorkDescription] = useState(
    (data && data.workDescription) || ""
  );
  const [locationDescription, setLocationDescription] = useState(
    (data && data.locationDescription) || ""
  );

  const [formErrors, _setFormErrors] = useState({
    district: null,
    ea: null,
    workDescription: null,
  });

  const validateForm = () => {
    const standardError = "Required";
    const _formErrors = {
      district: selectedDistrict ? null : standardError,
      ea: (ea || "").length === 6 ? null : standardError,
      workDescription: workDescription ? null : standardError,
    };
    _setFormErrors(_formErrors);
  };

  const [pmChildErrors, setPmChildErrors] = useState({});
  const setPmChildError = useCallback(
    (key, hasError) =>
      setPmChildErrors((prev) => {
        if (prev[key] === hasError) return prev;
        return { ...prev, [key]: hasError };
      }),
    []
  );

  // Let the parent know of errors — combine own field errors with PM row errors
  useEffect(
    () =>
      setFormIsIncomplete(
        Object.values(formErrors).some((e) => e) ||
          Object.values(pmChildErrors).some(Boolean)
      ),
    [formErrors, pmChildErrors]
  );

  useEffect(() => validateForm(), [selectedDistrict]);

  const handleChange_select = (fieldName, selectedOption, stateSetter) => {
    validateForm();
    stateSetter(selectedOption);
    update(fieldName, selectedOption ? selectedOption.value : 0);
  };

  const handleBlur = (fieldName, value) => {
    validateForm();
    update(fieldName, value);
  };

  return (
    <div className="card m-2">
      <div className="card-body">
        <div className="row pb-2">
          <h4 className="ml-2">Basic Project Information</h4>
        </div>
        <h5 className="text-muted mx-0 mb-0">EA, EFIS, Fed ID</h5>
        <hr className="mt-1 mb-2" />
        <div className="form-row">
          <div className="form-group col-md-2">
            <label htmlFor="district" className="mb-0">
              <b>District:</b>*
            </label>
            <Select
              name="district"
              id="district"
              isSearchable={false}
              value={selectedDistrict}
              options={projDistOptions.current}
              onChange={(selected) =>
                handleChange_select("district", selected, selectDistrict)
              }
              isClearable
              isDisabled={data && data.isPRSMListed}
            />
            {formErrors.district && (
              <span className="error-small">
                <small>{formErrors.district}</small>
              </span>
            )}
          </div>
          <div className="form-group col-md-2">
            <label htmlFor="ea" className="mb-0">
              <b>EA:</b>* <small>6-digit EA</small>
            </label>
            <input
              type="text"
              name="ea"
              id="ea"
              maxLength="6"
              className="form-control"
              value={ea}
              onChange={(e) => setEa(e.target.value)}
              onBlur={(e) => handleBlur("ea", e.target.value)}
              disabled={data && data.isPRSMListed}
            />
            {formErrors.ea && (
              <span className="error-small">
                <small>{formErrors.ea}</small>
              </span>
            )}
          </div>
          <div className="form-group col-md-3">
            <label htmlFor="eFis" className="mb-0">
              EFIS Project ID:
            </label>
            <input
              type="text"
              name="efis"
              id="efis"
              className="form-control"
              maxLength="10"
              value={efis}
              onChange={(e) => setEfis(e.target.value)}
              onBlur={(e) => handleBlur("efis", e.target.value)}
            />
          </div>
          <div className="form-group col-md-5">
            <label htmlFor="fedId" className="mb-0">
              Federal Aid No(s):
              <small className="ml-1">Separate by commas for multiple.</small>
            </label>
            <input
              type="text"
              name="fedId"
              id="fedId"
              className="form-control"
              value={fedId}
              onChange={(e) => setFedId(e.target.value)}
              onBlur={(e) => update("fedId", e.target.value)}
            />
          </div>
        </div>
        <h5 className="text-muted mx-0 mb-0">Project Status, Type</h5>
        <hr className="mt-1 mb-2" />
        <div className="form-row">
          <div className="form-group col-md-3">
            <label htmlFor="projectStatus" className="mb-0">
              Project Status:
            </label>
            <Select
              name="projectStatus"
              id="projectStatus"
              value={selectedStatus}
              options={projStatusOptions.current}
              onChange={(selected) =>
                handleChange_select("projectStatus", selected, selectStatus)
              }
              isClearable
            />
          </div>
          <div className="form-group col-md-5">
            <label htmlFor="projectType" className="mb-0">
              Project Type:
            </label>
            <Select
              name="projectType"
              id="projectType"
              value={selectedType}
              options={projTypeOptions || []}
              onChange={(selected) =>
                handleChange_select("projectType", selected, selectType)
              }
              isClearable
            />
          </div>
          {showContractNo && (
            <div className="form-group col-md-4">
              <label htmlFor="contractNo" className="mb-0">
                Contract No:
              </label>
              <input
                name="contractNo"
                id="contractNo"
                value={contractNo}
                onChange={(e) => setContractNo(e.target.value)}
                onBlur={(e) => handleBlur(e.target.name, e.target.value)}
                className="form-control"
                maxLength="15"
              />
            </div>
          )}
        </div>
        <h5 className="text-muted mx-0 mb-0 mt-2">County-Route-PM</h5>
        <hr className="mt-1 mb-2" />
        <EditProjectModalV2_PostMileRow
          pmData={data.postMiles1}
          pmSequence={1}
          countyOptions={countyOptions}
          update={(fieldName, value) => {
            update("postMiles1", { ...(data.postMiles1 || {}), [fieldName]: value });
          }}
          setFormIsIncomplete={(v) => setPmChildError("pm1", v)}
        />
        <EditProjectModalV2_PostMileRow
          pmData={data.postMiles2}
          pmSequence={2}
          countyOptions={countyOptions}
          update={(fieldName, value) => {
            update("postMiles2", { ...(data.postMiles2 || {}), [fieldName]: value });
          }}
          setFormIsIncomplete={(v) => setPmChildError("pm2", v)}
        />
        <EditProjectModalV2_PostMileRow
          pmData={data.postMiles3}
          pmSequence={3}
          countyOptions={countyOptions}
          update={(fieldName, value) => {
            update("postMiles3", { ...(data.postMiles3 || {}), [fieldName]: value });
          }}
          setFormIsIncomplete={(v) => setPmChildError("pm3", v)}
        />
        <EditProjectModalV2_PostMileRow
          pmData={data.postMiles4}
          pmSequence={4}
          countyOptions={countyOptions}
          update={(fieldName, value) => {
            update("postMiles4", { ...(data.postMiles4 || {}), [fieldName]: value });
          }}
          setFormIsIncomplete={(v) => setPmChildError("pm4", v)}
        />
        <h5 className="text-muted mx-0 mb-0 mt-3">Work and Location Descriptions</h5>
        <hr className="mt-1 mb-2" />
        <div className="form-row">
          <div className="form-group col-md-12">
            <label htmlFor="workDescription" className="mb-0">
              <b>Work Description:</b>* <small>i.e. Widen Freeway</small>
            </label>
            <input
              type="text"
              name="workDescription"
              id="workDescription"
              className="form-control"
              value={workDescription}
              onChange={(e) => setWorkDescription(e.target.value)}
              onBlur={(e) => handleBlur(e.target.name, e.target.value)}
            />
            {formErrors.workDescription && (
              <span className="error-small">
                <small>{formErrors.workDescription}</small>
              </span>
            )}
          </div>
        </div>
        <div className="form-row">
          <div className="form-group col-md-12">
            <label htmlFor="locationDescription" className="mb-0">
              Location Description:
              <small className="ml-1">
                i.e. Construction on State Highway in Marin County, ...
              </small>
            </label>
            <textarea
              rows="2"
              name="locationDescription"
              id="locationDescription"
              className="form-control"
              value={locationDescription}
              onChange={(e) => setLocationDescription(e.target.value)}
              onBlur={(e) => handleBlur(e.target.name, e.target.value)}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

function EditProjectModalV2_PostMileRow(props) {
  const { pmData, pmSequence, countyOptions, update, setFormIsIncomplete } = props;

  const [selectedCounty, selectCounty] = useState(
    pmData && pmData.county
      ? (countyOptions.current || []).find(
          (c) => c.value.toLowerCase() === pmData.county.toLowerCase()
        ) || null
      : null
  );
  const [route, setRoute] = useState((pmData && pmData.route) || "");
  const [backPostMilePrefix, setBackPostMilePrefix] = useState(
    (pmData && pmData.backPostMilePrefix) || ""
  );
  const [backPostMile, setBackPostMile] = useState(
    pmData && pmData.backPostMile != null ? String(pmData.backPostMile) : ""
  );
  const [aheadPostMilePrefix, setAheadPostMilePrefix] = useState(
    (pmData && pmData.aheadPostMilePrefix) || ""
  );
  const [aheadPostMile, setAheadPostMile] = useState(
    pmData && pmData.aheadPostMile != null ? String(pmData.aheadPostMile) : ""
  );

  const [formErrors, _setFormErrors] = useState({
    county1: null,
    route1: null,
    backPostMile1: null,
  });

  const validateForm = useCallback(() => {
    const standardError = "Required";
    const _formErrors = {
      county1: pmSequence === 1 && !selectedCounty ? standardError : null,
      route1: pmSequence === 1 && !route ? standardError : null,
      backPostMile1: pmSequence === 1 && backPostMile === "" ? standardError : null,
    };
    _setFormErrors(_formErrors);
  }, [selectedCounty, route, backPostMile]);

  useEffect(() => validateForm(), [selectedCounty, route, backPostMile]);

  // Let the parent know of errors
  useEffect(
    () => setFormIsIncomplete(Object.values(formErrors).some((e) => e)),
    [formErrors]
  );

  const handleChange_county = (selectedOption) => {
    selectCounty(selectedOption);
    update("county", selectedOption ? selectedOption.value : "");
  };

  const handleBlur = (fieldName, value) => update(fieldName, value);

  return (
    <div className="form-row mb-1">
      <div className="form-group col-md-4 mb-0">
        <label htmlFor={`county-${pmSequence}`} className="mb-0">
          <span className={`${pmSequence === 1 ? "font-weight-bold" : ""}`}>
            {`County ${pmSequence}`}:
          </span>
          {`${pmSequence === 1 ? "*" : ""}`}
        </label>
        <Select
          name="county"
          id={`county-${pmSequence}`}
          value={selectedCounty}
          options={countyOptions.current || []}
          onChange={handleChange_county}
          isClearable
        />
        {formErrors.county1 && (
          <span className="error-small">
            <small>{formErrors.county1}</small>
          </span>
        )}
      </div>
      <div className="form-group col-md-2 mb-0">
        <label htmlFor={`route-${pmSequence}`} className="mb-0">
          <span className={`${pmSequence === 1 ? "font-weight-bold" : ""}`}>
            {`Route ${pmSequence}`}:
          </span>
          {`${pmSequence === 1 ? "*" : ""}`}
        </label>
        <input
          type="text"
          name="route"
          id={`route-${pmSequence}`}
          className="form-control"
          value={route}
          onChange={(e) => setRoute(e.target.value)}
          onBlur={(e) => handleBlur(e.target.name, e.target.value)}
          disabled={!selectedCounty}
        />
        {formErrors.route1 && (
          <span className="error-small">
            <small>{formErrors.route1}</small>
          </span>
        )}
      </div>
      <div className="form-group col-md-2 mb-0">
        <label htmlFor={`backPostMile-${pmSequence}`} className="mb-0">
          <span className={`${pmSequence === 1 ? "font-weight-bold" : ""}`}>
            {`Back PM ${pmSequence}:`}
          </span>
          {`${pmSequence === 1 ? "*" : ""}`}
        </label>
        <div className="input-group">
          <select
            name="backPostMilePrefix"
            id={`backPostMilePrefix-${pmSequence}`}
            className="form-control"
            style={{ maxWidth: "5rem" }}
            value={backPostMilePrefix}
            onChange={(e) => {
              setBackPostMilePrefix(e.target.value);
              update("backPostMilePrefix", e.target.value);
            }}
            disabled={!selectedCounty || !route}
          >
            <option value=""></option>
            {PM_PREFIX_OPTIONS.map((p) => (
              <option key={p} value={p}>{p}</option>
            ))}
          </select>
          <input
            type="number"
            name="backPostMile"
            id={`backPostMile-${pmSequence}`}
            className="form-control"
            value={backPostMile}
            onChange={(e) => setBackPostMile(e.target.value)}
            onBlur={(e) => handleBlur(e.target.name, e.target.value)}
            disabled={!selectedCounty || !route}
          />
        </div>
        {formErrors.backPostMile1 && (
          <span className="error-small">
            <small>{formErrors.backPostMile1}</small>
          </span>
        )}
      </div>
      <div className="form-group col-md-2 mb-0">
        <label htmlFor={`aheadPostMile-${pmSequence}`} className="mb-0">
          {`Ahead PM ${pmSequence}:`}
        </label>
        <div className="input-group">
          <select
            name="aheadPostMilePrefix"
            id={`aheadPostMilePrefix-${pmSequence}`}
            className="form-control"
            style={{ maxWidth: "5rem" }}
            value={aheadPostMilePrefix}
            onChange={(e) => {
              setAheadPostMilePrefix(e.target.value);
              update("aheadPostMilePrefix", e.target.value);
            }}
            disabled={!route || !selectedCounty}
          >
            <option value=""></option>
            {PM_PREFIX_OPTIONS.map((p) => (
              <option key={p} value={p}>{p}</option>
            ))}
          </select>
          <input
            type="number"
            name="aheadPostMile"
            id={`aheadPostMile-${pmSequence}`}
            className="form-control"
            value={aheadPostMile}
            onChange={(e) => setAheadPostMile(e.target.value)}
            onBlur={(e) => handleBlur(e.target.name, e.target.value)}
            disabled={!route || !selectedCounty}
          />
        </div>
      </div>
    </div>
  );
}

function EditProjectModalV2_OtherDetails(props) {
  const { data, update, setFormIsIncomplete } = props;

  const calendarTypes = useRef([
    { value: "5d", label: "5-day" },
    { value: "7d", label: "7-day" },
  ]);

  const [selectedCalendarType, selectCalendarType] = useState(
    data.calendarType
      ? calendarTypes.current.find((ct) => ct.value === data.calendarType) || null
      : null
  );
  const [workingDays, setWorkingDays] = useState(data.workingDays || "");
  const [bidOpeningDate, setBidOpeningDate] = useState(
    data.bidOpeningDate ? new Date(data.bidOpeningDate) : null
  );
  const [contractAwardDate, setContractAwardDate] = useState(
    data.contractAwardDate ? new Date(data.contractAwardDate) : null
  );
  const [contractApprovalDate, setContractApprovalDate] = useState(
    data.contractApprovalDate ? new Date(data.contractApprovalDate) : null
  );
  const [firstWorkingDay, setFirstWorkingDay] = useState(
    data.firstWorkingDay ? new Date(data.firstWorkingDay) : null
  );
  const [engineersEstimate, setEngineersEstimate] = useState(
    data.engineersEstimate || ""
  );
  const [useTroMarkup, setUseTroMarkup] = useState(data.useTroMarkup || false);
  const [primeLaborMarkup, setPrimeLaborMarkup] = useState(data.primeLaborMarkup || "");
  const [primeEquipmentMarkup, setPrimeEquipmentMarkup] = useState(
    data.primeEquipmentMarkup || ""
  );
  const [primeMaterialMarkup, setPrimeMaterialMarkup] = useState(
    data.primeMaterialMarkup || ""
  );
  const [subcontractorMarkup, setSubcontractorMarkup] = useState(
    data.subcontractorMarkup || ""
  );
  const [subLaborMarkup, setSubLaborMarkup] = useState(data.subLaborMarkup || "");
  const [subEquipmentMarkup, setSubEquipmentMarkup] = useState(
    data.subEquipmentMarkup || ""
  );
  const [subMaterialMarkup, setSubmaterialMarkup] = useState(
    data.subMaterialMarkup || ""
  );

  const [formErrors, _setFormErrors] = useState({
    primeLaborMarkup: null,
    primeEquipmentMarkup: null,
    primeMaterialMarkup: null,
    subcontractorMarkup: null,
    subLaborMarkup: null,
    subEquipmentMarkup: null,
    subMaterialMarkup: null,
  });

  const validateForm = (fieldName, value) => {
    switch (fieldName) {
      case "primeLaborMarkup":
      case "primeEquipmentMarkup":
      case "primeMaterialMarkup":
      case "subcontractorMarkup":
      case "subLaborMarkup":
      case "subEquipmentMarkup":
      case "subMaterialMarkup":
        _setFormErrors((prev) => ({
          ...prev,
          [fieldName]: +value > 100 ? "Invalid value" : null,
        }));
        break;
      default:
        break;
    }
  };

  useEffect(
    () => setFormIsIncomplete(Object.values(formErrors).some((e) => e)),
    [formErrors]
  );

  const handleChange_select = (fieldName, selectedOption, stateSetter) => {
    stateSetter(selectedOption);
    update(fieldName, selectedOption ? selectedOption.value : 0);
  };

  const handleBlur = (fieldName, value) => {
    validateForm(fieldName, value);
    update(fieldName, value);
  };

  return (
    <div className="card m-2">
      <div className="card-body">
        <div className="row pb-2">
          <h4 className="ml-2">Other Details</h4>
        </div>
        <h5 className="text-muted mx-0 mb-0">Working Days</h5>
        <hr className="mt-1 mb-2" />
        <div className="form-row">
          <div className="form-group col-sm-2">
            <label htmlFor="calendarType" className="mb-0">
              WD Type:
            </label>
            <Select
              name="calendarType"
              id="calendarType"
              isSearchable={false}
              value={selectedCalendarType}
              options={calendarTypes.current}
              onChange={(selected) =>
                handleChange_select("calendarType", selected, selectCalendarType)
              }
            />
          </div>
          <div className="form-group col-sm-3">
            <label htmlFor="workingDays" className="mb-0">
              Working Days:
            </label>
            <div className="input-group">
              <input
                type="number"
                name="workingDays"
                id="workingDays"
                className="form-control"
                value={workingDays || 0}
                onChange={(e) => setWorkingDays(e.target.value)}
                onBlur={(e) => update(e.target.name, e.target.value)}
              />
              <div className="input-group-append">
                <span className="input-group-text">Days</span>
              </div>
            </div>
          </div>
        </div>
        <h5 className="text-muted mx-0 mb-0 mt-2">Milestone Dates</h5>
        <hr className="mt-1 mb-2" />
        <div className="form-row">
          <div className="form-group col-sm-3">
            <label htmlFor="bidOpeningDate" className="mb-0">
              Bid Opening:
            </label>
            <DatePicker
              customInput={<CustomDatePickerInput />}
              name="bidOpeningDate"
              id="bidOpeningDate"
              className="form-control"
              selected={bidOpeningDate}
              onChange={(date) => {
                setBidOpeningDate(date);
                update("bidOpeningDate", date);
              }}
            />
          </div>
          <div className="form-group col-sm-3">
            <label htmlFor="awardDate" className="mb-0">
              Award:
            </label>
            <DatePicker
              customInput={<CustomDatePickerInput />}
              name="contractAwardDate"
              id="contractAwardDate"
              className="form-control"
              selected={contractAwardDate}
              onChange={(date) => {
                setContractAwardDate(date);
                update("contractAwardDate", date);
              }}
            />
          </div>
          <div className="form-group col-sm-3">
            <label htmlFor="contractApprovalDate" className="mb-0">
              Approval:
            </label>
            <DatePicker
              customInput={<CustomDatePickerInput />}
              name="contractApprovalDate"
              id="contractApprovalDate"
              className="form-control"
              selected={contractApprovalDate}
              onChange={(date) => {
                setContractApprovalDate(date);
                update("contractApprovalDate", date);
              }}
            />
          </div>
          <div className="form-group col-sm-3">
            <label htmlFor="workStartDate" className="mb-0">
              1st WD:
            </label>
            <DatePicker
              customInput={<CustomDatePickerInput />}
              name="firstWorkingDay"
              id="firstWorkingDay"
              className="form-control"
              selected={firstWorkingDay}
              onChange={(date) => {
                setFirstWorkingDay(date);
                update("firstWorkingDay", date);
              }}
            />
          </div>
        </div>
        <h5 className="text-muted mx-0 mb-0 mt-2">Cost and Markups</h5>
        <hr className="mt-1 mb-2" />
        <div className="form-row">
          <div className="form-group col-sm-6">
            <label htmlFor="engineersEstimate" className="mb-0">
              Engineers Estimate:
            </label>
            <div className="input-group">
              <div className="input-group-prepend">
                <span className="input-group-text">$</span>
              </div>
              <input
                type="number"
                name="engineersEstimate"
                id="engineersEstimate"
                className="form-control"
                value={engineersEstimate || 0}
                onChange={(e) => setEngineersEstimate(e.target.value)}
                onBlur={(e) => handleBlur(e.target.name, e.target.value)}
              />
            </div>
          </div>
        </div>
        <div className="form-row mb-2">
          <div className="form-check-inline custom-control custom-checkbox my-auto ml-1">
            <input
              type="checkbox"
              name="useTroMarkup"
              id="useTroMarkup"
              className="custom-control-input"
              checked={useTroMarkup}
              onChange={(e) => {
                setUseTroMarkup(e.target.checked);
                update("useTroMarkup", e.target.checked);
              }}
            />
            <label className="custom-control-label" htmlFor="useTroMarkup">
              Use TRO Markup
            </label>
          </div>
        </div>
        <div className="form-row">
          <div className="form-group col-md-3 mb-0">
            <label htmlFor="primeLaborMarkup" className="mb-0">
              Prime Labor Markup:
            </label>
            <div className="input-group">
              <input
                type="number"
                name="primeLaborMarkup"
                id="primeLaborMarkup"
                className="form-control"
                style={{ textAlign: "right" }}
                value={primeLaborMarkup || ""}
                onChange={(e) => setPrimeLaborMarkup(e.target.value)}
                onBlur={(e) => handleBlur(e.target.name, e.target.value)}
              />
              <div className="input-group-append">
                <span className="input-group-text">%</span>
              </div>
            </div>
            {formErrors.primeLaborMarkup && (
              <span className="error-small">
                <small>{formErrors.primeLaborMarkup}</small>
              </span>
            )}
          </div>
          <div className="form-group col-md-3 mb-0">
            <label htmlFor="primeEquipmentMarkup" className="mb-0">
              Prime Equipment Markup:
            </label>
            <div className="input-group">
              <input
                type="number"
                name="primeEquipmentMarkup"
                id="primeEquipmentMarkup"
                className="form-control"
                style={{ textAlign: "right" }}
                value={primeEquipmentMarkup || ""}
                onChange={(e) => setPrimeEquipmentMarkup(e.target.value)}
                onBlur={(e) => handleBlur(e.target.name, e.target.value)}
              />
              <div className="input-group-append">
                <span className="input-group-text">%</span>
              </div>
            </div>
            {formErrors.primeEquipmentMarkup && (
              <span className="error-small">
                <small>{formErrors.primeEquipmentMarkup}</small>
              </span>
            )}
          </div>
          <div className="form-group col-md-3 mb-0">
            <label htmlFor="primeMaterialMarkup" className="mb-0">
              Prime Mat'l Markup:
            </label>
            <div className="input-group">
              <input
                type="number"
                name="primeMaterialMarkup"
                id="primeMaterialMarkup"
                className="form-control"
                style={{ textAlign: "right" }}
                value={primeMaterialMarkup || ""}
                onChange={(e) => setPrimeMaterialMarkup(e.target.value)}
                onBlur={(e) => handleBlur(e.target.name, e.target.value)}
              />
              <div className="input-group-append">
                <span className="input-group-text">%</span>
              </div>
            </div>
            {formErrors.primeMaterialMarkup && (
              <span className="error-small">
                <small>{formErrors.primeMaterialMarkup}</small>
              </span>
            )}
          </div>
        </div>
        <div className="form-row">
          <div className="form-group col-md-3 mb-0">
            <label htmlFor="subcontractorMarkup" className="mb-0">
              Sub Markup:
            </label>
            <div className="input-group">
              <input
                type="number"
                name="subcontractorMarkup"
                id="subcontractorMarkup"
                className="form-control"
                style={{ textAlign: "right" }}
                value={subcontractorMarkup || ""}
                onChange={(e) => setSubcontractorMarkup(e.target.value)}
                onBlur={(e) => handleBlur(e.target.name, e.target.value)}
              />
              <div className="input-group-append">
                <span className="input-group-text">%</span>
              </div>
            </div>
            {formErrors.subcontractorMarkup && (
              <span className="error-small">
                <small>{formErrors.subcontractorMarkup}</small>
              </span>
            )}
          </div>
        </div>
        <div className="form-row">
          <div className="form-group col-md-3 mb-0">
            <label htmlFor="subLaborMarkup" className="mb-0">
              Sub Labor Markup:
            </label>
            <div className="input-group">
              <input
                type="number"
                name="subLaborMarkup"
                id="subLaborMarkup"
                className="form-control"
                style={{ textAlign: "right" }}
                value={subLaborMarkup || ""}
                onChange={(e) => setSubLaborMarkup(e.target.value)}
                onBlur={(e) => handleBlur(e.target.name, e.target.value)}
              />
              <div className="input-group-append">
                <span className="input-group-text">%</span>
              </div>
            </div>
            {formErrors.subLaborMarkup && (
              <span className="error-small">
                <small>{formErrors.subLaborMarkup}</small>
              </span>
            )}
          </div>
          <div className="form-group col-md-3 mb-0">
            <label htmlFor="subEquipmentMarkup" className="mb-0">
              Sub Equipment Markup:
            </label>
            <div className="input-group">
              <input
                type="number"
                name="subEquipmentMarkup"
                id="subEquipmentMarkup"
                className="form-control"
                style={{ textAlign: "right" }}
                value={subEquipmentMarkup || ""}
                onChange={(e) => setSubEquipmentMarkup(e.target.value)}
                onBlur={(e) => handleBlur(e.target.name, e.target.value)}
              />
              <div className="input-group-append">
                <span className="input-group-text">%</span>
              </div>
            </div>
            {formErrors.subEquipmentMarkup && (
              <span className="error-small">
                <small>{formErrors.subEquipmentMarkup}</small>
              </span>
            )}
          </div>
          <div className="form-group col-md-3 mb-0">
            <label htmlFor="subMaterialMarkup" className="mb-0">
              Sub Mat'l Markup:
            </label>
            <div className="input-group">
              <input
                type="number"
                name="subMaterialMarkup"
                id="subMaterialMarkup"
                className="form-control"
                style={{ textAlign: "right" }}
                value={subMaterialMarkup || ""}
                onChange={(e) => setSubmaterialMarkup(e.target.value)}
                onBlur={(e) => handleBlur(e.target.name, e.target.value)}
              />
              <div className="input-group-append">
                <span className="input-group-text">%</span>
              </div>
            </div>
            {formErrors.subMaterialMarkup && (
              <span className="error-small">
                <small>{formErrors.subMaterialMarkup}</small>
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
